import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import PropTypes from "prop-types";
import { Typography, styled } from "@material-ui/core";
import DescriptionIcon from "@material-ui/icons/Description";
import icone from "../../assets/images/uploadArrow.png";

import { lang } from "../../i18nLang";

const DropzoneWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "240px",
  border: `1px solid ${theme.palette.gray.main}`,
  boxSizing: "border-box",
  borderRadius: 4,
  cursor: "pointer",
}));

const DropzoneBody = styled("div")(({ theme }) => ({
  height: "190px",
  backgroundColor: theme.palette.primary.main,
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const DropzoneFooter = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Legend = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 500,
  textAlign: "center",
}));

const FileIcon = styled(DescriptionIcon)({
  fontSize: "4rem",
  color: "white",
});

const UploadIcon = styled("img")({
  height: "auto",
  width: "auto",
  maxHeight: "100%",
});

interface IProps {
  [property: string]: any;

  onDrop: Function;
  accept: string;
  maxFiles?: number;
  currentFiles?: File[];
}

const Dropzone = ({
  onDrop,
  accept,
  maxFiles,
  currentFiles,
  ...props
}: IProps) => {
  const [files, setFiles] = useState(new Array<File>());
  const handleDrop = (acceptedFiles: any[], rejectedFiles: any[], event) => {
    setFiles(acceptedFiles);
    onDrop(acceptedFiles, rejectedFiles);
  };

  const { fileRejections, getRootProps, getInputProps, isDragActive } =
    useDropzone({ onDrop: handleDrop, accept, maxFiles });

  useEffect(() => {
    if (currentFiles) setFiles(currentFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasFiles = Boolean(files?.length);
  const rejection = fileRejections.find(({ file, errors }) => errors?.length);

  return (
    <DropzoneWrapper {...props} {...getRootProps()}>
      <DropzoneBody>
        <input data-testid="dropzone" {...getInputProps()} />
        {hasFiles ? <FileIcon /> : <UploadIcon src={icone} alt="Upload" />}
      </DropzoneBody>
      <DropzoneFooter>
        {isDragActive ? (
          <Legend>{lang("dropzone.dropHere")}</Legend>
        ) : !hasFiles && !rejection ? (
          <Legend>{lang("dropzone.empty")}</Legend>
        ) : (
          <>
            {files.map((file: any) => (
              <Legend key={file.path}>{file.path}</Legend>
            ))}

            {rejection &&
              rejection.errors.map((e) => (
                <Legend key={e.code}>{e.message}</Legend>
              ))}
          </>
        )}
      </DropzoneFooter>
    </DropzoneWrapper>
  );
};

Dropzone.propTypes = {
  onDrop: PropTypes.func.isRequired,
  accept: PropTypes.string.isRequired,
  maxFiles: PropTypes.number.isRequired,
};

Dropzone.defaultProps = {
  maxFiles: 1,
};

export default Dropzone;
